import { ComponentChildren, h, JSX } from "preact"
import { useContext } from "preact/hooks"
import { SearchQuery } from "../../api"
import { trackGaPageView } from "../../helpers/ga"
import { paramsToQueryString } from "../../urlEncoding/url"
import { useConfig } from "../../hooks"
import { Context } from "./Context"
import { AutocompleteOptions } from "./types"
import { useAppStateSelector } from "../../hooks/useAppStateSelector"
import { nostojs } from "@nosto/nosto-js"
import { logger } from "@nosto/search-js/utils"

export function AutocompleteElementProvider(props: {
    onSubmit(query: SearchQuery, options?: AutocompleteOptions): void
    children: ComponentChildren
}) {
    return <Context.Provider value={props.onSubmit}>{props.children}</Context.Provider>
}

/**
 * @group Components
 */
export type AutocompleteElementProps = {
    /**
     * Rendered content.
     */
    children: ComponentChildren
    /**
     * Execute new search with specified query on the selection
     */
    query?: SearchQuery
    /**
     * Append CSS class to element
     */
    class?: string
    /**
     * Append CSS class to element is selected with keyboard
     */
    activeClass?: string
    /**
     * Get product data for analytics and redirection
     */
    hit?: {
        productId: string
        url?: string
        keyword?: string
        _redirect?: string
    }
    /**
     * Filter to use on keyword click
     */
    filter?: {
        field: string
        value: string[]
    }
    /**
     * Redirect to specified url on click
     */
    url?: string
    /**
     * Render element as specified element
     */
    as?: string
    /**
     * Handle click event
     */
    onClick: (event: Event) => void
} & JSX.HTMLAttributes &
    JSX.SVGAttributes

/**
 * Wraps elements to interactive component that handles selection based on provided configuration.
 *
 * @example
 * ```jsx
 * // Render product
 * {products?.hits?.map(hit => <AutocompleteElement hit={hit} class="nosto-product">
 *     {hit.title}
 * </AutocompleteElement>)}
 *
 * // Render keywords
 * {keywords?.hits?.map(hit => <AutocompleteElement query={{ query: hit.keywords }} class="nosto-keyword">
 *     {hit.keywords}
 * </AutocompleteElement>)}
 * ```
 *
 * @group Components
 */
export function AutocompleteElement(props: AutocompleteElementProps): JSX.Element {
    const { as, filter, children, activeClass, url, hit, onClick, ...rest } = props
    const query = useAppStateSelector(state => state.query.query)
    const { serpUrlMapping, serpPath } = useConfig()
    const onSubmit = useContext(Context)

    const searchOptions = {
        isKeyword: true
    } as const

    const getTrackUrl = (value: string | undefined) => {
        try {
            return new URL(
                `${serpPath || location.pathname}?${paramsToQueryString({
                    [serpUrlMapping.query || "query"]: value
                })}`,
                window.location.origin
            ).toString()
        } catch (error) {
            logger.warn("Could not create track url", error)
            return undefined
        }
    }

    function handleClick() {
        if (props.hit) {
            nostojs(api => api.recordSearchClick("autocomplete", props.hit!))

            if (props.hit?.keyword) {
                if (props.hit?._redirect) {
                    trackGaPageView({
                        delay: true,
                        location: getTrackUrl(props.hit.keyword)
                    })
                    location.href = props.hit._redirect
                    return
                }

                return onSubmit(
                    {
                        query: props.hit?.keyword,
                        ...(props.filter
                            ? {
                                  products: {
                                      filter: [props.filter]
                                  }
                              }
                            : {})
                    },
                    searchOptions
                )
            }
            trackGaPageView({
                delay: true,
                location: getTrackUrl(query)
            })
        }

        if (props?.hit?.url || props?.url) {
            location.href = (props.url || props?.hit?.url)!
        } else if (props.query) {
            onSubmit(
                {
                    ...props.query,
                    ...(props.filter
                        ? {
                              products: {
                                  filter: [props.filter]
                              }
                          }
                        : {})
                },
                searchOptions
            )
        }
    }

    if (as) {
        return h(
            as,
            {
                ...(as === "a" && props.hit?.url
                    ? {
                          href: props.hit.url
                      }
                    : {}),
                ...rest,
                onClick: (event: MouseEvent) => {
                    if (props.hit) {
                        nostojs(api => api.recordSearchClick("autocomplete", props.hit!))
                    }
                    return props.onClick?.(event)
                }
            },
            props.children
        )
    }

    return (
        <div
            class={`ns-autocomplete-element ${props.class ?? ""}`}
            data-nosto-query={props?.hit?.keyword || props?.query?.query}
            data-nosto-url={props.url || props?.hit?.url}
            onClick={() => {
                handleClick()
            }}
            onKeyDown={(event: KeyboardEvent) => {
                if (event.key === "Enter") {
                    handleClick()
                }
            }}
        >
            {props.children}
        </div>
    )
}
